import { Link } from 'react-router-dom';
import PageShell from '../components/PageShell';
import PolicyLayout from './PolicyLayout';
import {
  ADDRESS,
  BRAND_NAME,
  COMPANY_NAME,
  POLICY_UPDATED,
  SITE_DOMAIN,
  SUPPORT_PHONE_LABEL,
  SUPPORT_PHONE_TEL,
} from '../constants/site';

export default function PrivacyPolicy() {
  return (
    <PageShell>
      <PolicyLayout
        title="Privacy Policy"
        updated={POLICY_UPDATED}
        intro={`This notice describes how ${COMPANY_NAME}, the business behind ${BRAND_NAME} (${SITE_DOMAIN}), handles personal details. Wherever you see "we," "our," or "us" below, it refers to ${COMPANY_NAME}. The sections that follow cover what reaches us, why we need it, which outside vendors can see it, and the controls that stay in your hands.`}
      >
        <h2>Privacy at a Glance</h2>
        <ul>
          <li>Your personal data is never sold, rented, or traded to anyone.</li>
          <li>A technician can see your screen only after you approve the connection.</li>
          <li>You may ask for a copy of your records, a correction, or deletion whenever you like.</li>
        </ul>

        <h2>Part A: What Reaches Us</h2>
        <h3>Things you tell us directly</h3>
        <ul>
          <li>
            Contact details such as your name, email, and phone number, typed into one of our
            request forms.
          </li>
          <li>
            A description of the trouble you&apos;re having, along with your Family Tree Maker
            edition and whether you use Windows or a Mac.
          </li>
          <li>Whatever you write or say to us over chat, email, or the phone.</li>
          <li>
            Billing information entered at checkout, which goes to our card processor rather than
            being kept on our own systems.
          </li>
        </ul>
        <h3>Technical data picked up as you browse</h3>
        <ul>
          <li>Network and device facts, for example IP address, browser name, and OS version.</li>
          <li>Which pages you open, how long each one stays on screen, and the referring link.</li>
          <li>Cookie identifiers and comparable tools that keep the site running and count visits.</li>
        </ul>
        <h3>While a technician is connected</h3>
        <p>
          Screen sharing starts only once you agree to it. Our technician looks at just the areas of
          your computer connected to the problem you described, and we don&apos;t duplicate, save, or
          browse your genealogy files or other private documents.
        </p>

        <h2>Part B: Chat Transcripts and Phone Calls</h2>
        <p>
          Our live chat vendor keeps a written log of each conversation. That record lets another
          technician pick up where the last one left off, so you don&apos;t have to repeat yourself. We
          treat transcripts the same way as any other support record described in this notice.
        </p>
        <p>
          Some calls may be recorded so we can review service quality and train new staff. When a call
          is being recorded, you&apos;ll be told at the start, and you&apos;re welcome to ask the
          technician to continue without recording.
        </p>

        <h2>Part C: Reasons We Use Your Details</h2>
        <ul>
          <li>Answering questions and carrying out the repair or setup work you requested.</li>
          <li>Checking back with you on tickets that haven&apos;t been closed yet.</li>
          <li>Collecting payment and keeping an accurate history of completed work.</li>
          <li>Making our guides, pages, and technician training better over time.</li>
          <li>Spotting and blocking fraud, spam, and attempted attacks on the site.</li>
          <li>Satisfying tax, accounting, and other legal obligations.</li>
        </ul>

        <h2>Part D: Cookies and Site Analytics</h2>
        <p>
          A cookie is a small text file your browser saves for a website. Some of ours are strictly
          necessary for pages to load and forms to submit; others tell us, in aggregate, which pages
          visitors find helpful. The chat widget may add its own cookie so an open conversation
          survives a page refresh. Your browser settings let you refuse or erase cookies at any point,
          but a few features, including chat, may stop behaving correctly if you do.
        </p>

        <h2>Part E: Vendors and Other Disclosures</h2>
        <p>Only a small set of outside companies that help run our operation receive personal data:</p>
        <ul>
          <li>The provider that powers our chat window and customer messages.</li>
          <li>The ticketing and lead-tracking platform where support requests are logged.</li>
          <li>Card processors that handle payment for paid sessions and plans.</li>
          <li>Companies that host this site and supply traffic statistics.</li>
        </ul>
        <p>
          Each vendor is limited to using the data for the task we hired it to do. Apart from these
          vendors, we would release information only when a valid legal demand requires it, or when
          doing so is necessary to defend our business, our customers, or the public from harm.
        </p>

        <h2>Part F: Retention Periods</h2>
        <p>
          Records stay with us for as long as they serve a real purpose, such as finishing your support
          case, maintaining bookkeeping, handling a disagreement, or meeting a legal requirement. Once
          that purpose ends, the data is erased or stripped of anything that identifies you.
        </p>

        <h2>Part G: Safeguards</h2>
        <p>
          Traffic between your browser and this site is encrypted over HTTPS, and access to customer
          records is limited to people who need it for their work. Even so, no system connected to the
          internet can be made perfectly safe, and we can&apos;t promise that a breach will never occur.
        </p>

        <h2>Part H: Controls You Keep</h2>
        <p>Based on your state or country of residence, you can typically ask us to:</p>
        <ul>
          <li>Show you what personal data we currently have on file for you.</li>
          <li>Fix anything in those records that is wrong or out of date.</li>
          <li>Erase your data when we no longer have a legal reason to retain it.</li>
          <li>Remove you from promotional emails or calls, effective right away.</li>
          <li>
            Record an opt-out from any sale or sharing of personal data, even though no sale takes
            place.
          </li>
        </ul>
        <p>
          Phone <a href={SUPPORT_PHONE_TEL}>{SUPPORT_PHONE_LABEL}</a> or send a message through our{' '}
          <Link to="/contact-us">contact form</Link> to get started. Before acting on a request, we may
          ask a few questions to make sure it really comes from you.
        </p>

        <h2>Part I: Young Visitors</h2>
        <p>
          This site and our services are meant for adults, and we don&apos;t knowingly gather data
          from anyone younger than 13. A parent or guardian who believes a child has sent us personal
          details can let us know, and we&apos;ll erase them promptly.
        </p>

        <h2>Part J: Outside Websites</h2>
        <p>
          Some pages point to sites we don&apos;t run, such as software publishers and online family
          history services. Their handling of personal data is governed by their own policies, which we
          encourage you to read before sharing anything with them.
        </p>

        <h2>Part K: Revisions to This Notice</h2>
        <p>
          As our services or the law change, this page may be edited. Check the date shown near the
          title to see when the most recent revision took effect.
        </p>

        <h2>Part L: Privacy Questions</h2>
        <p>
          <strong>{COMPANY_NAME}</strong>
          <br />
          Mailing address: {ADDRESS}
          <br />
          Call: <a href={SUPPORT_PHONE_TEL}>{SUPPORT_PHONE_LABEL}</a>
          <br />
          Online: {SITE_DOMAIN}
        </p>
      </PolicyLayout>
    </PageShell>
  );
}
